import {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';

interface Customer {
  id: string;
  name: string;
  email: string;
}

interface AuthState {
  authenticated: boolean;
  loading: boolean;
  customer: Customer | null;
  error: string | null;
}

type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; customer: Customer }
  | { type: 'LOGIN_FAILURE'; error: string }
  | { type: 'LOGOUT' }
  | { type: 'SESSION_CHECK_START' }
  | { type: 'SESSION_CHECK_SUCCESS'; customer: Customer }
  | { type: 'SESSION_CHECK_FAILURE' };

interface AuthContextValue extends AuthState {
  logout: () => Promise<void>;
  checkSession: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

const initialState: AuthState = {
  authenticated: false,
  loading: true,
  customer: null,
  error: null,
};

function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'LOGIN_START':
      return { ...state, loading: true, error: null };
    case 'LOGIN_SUCCESS':
      return { authenticated: true, loading: false, customer: action.customer, error: null };
    case 'LOGIN_FAILURE':
      return { authenticated: false, loading: false, customer: null, error: action.error };
    case 'LOGOUT':
      return { authenticated: false, loading: false, customer: null, error: null };
    case 'SESSION_CHECK_START':
      return { ...state, loading: true };
    case 'SESSION_CHECK_SUCCESS':
      return { authenticated: true, loading: false, customer: action.customer, error: null };
    case 'SESSION_CHECK_FAILURE':
      return { authenticated: false, loading: false, customer: null, error: null };
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const checkSession = useCallback(async () => {
    dispatch({ type: 'SESSION_CHECK_START' });
    try {
      const res = await fetch('/api/v1/auth/session', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        if (data.authenticated) {
          dispatch({ type: 'SESSION_CHECK_SUCCESS', customer: data.customer });
        } else {
          dispatch({ type: 'SESSION_CHECK_FAILURE' });
        }
      } else {
        dispatch({ type: 'SESSION_CHECK_FAILURE' });
      }
    } catch {
      dispatch({ type: 'SESSION_CHECK_FAILURE' });
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await fetch('/api/v1/auth/logout', { method: 'POST', credentials: 'include' });
    } finally {
      dispatch({ type: 'LOGOUT' });
    }
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  return (
    <AuthContext.Provider value={{ ...state, logout, checkSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
